<script setup lang="ts">
    import { ref, computed } from 'vue'

    import type {
        SelectionProjectReadModel,
        SelectionCriterionReadModel,
        SelectionComparableScenarioReadModel
    } from '@Quest/PortfolioScenarios/readmodels'

    import { getDistinctBgCss, getDistinctColor, numCompare } from '@common/Common'
    import { ProjectAcceptance } from '@common/types'

    import CriterionSelectorDialog from '@Criteria/Hierarchies/Components/CriterionSelectorDialog.vue'
    import { CriterionSelectorDialogViewModel } from '@Criteria/Hierarchies/Components/CriterionSelectorDialog'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        propCurrencyLabel: string
        propProjects: SelectionProjectReadModel[]
        propAllCriteria: SelectionCriterionReadModel[]
        propComparableScenarios: SelectionComparableScenarioReadModel[]
        propSearchPhrase?: string | null
        propProjectsFilter?: string | null
    }>()

    const emit = defineEmits<{
        (e: 'update-search-phrase', value: string | null): void
        (e: 'update-projects-filter', value: string): void
        (e: 'accept-selection', p: SelectionProjectReadModel): void
        (e: 'reject-selection', p: SelectionProjectReadModel): void
        (e: 'lock-selection', p: SelectionProjectReadModel): void
        (e: 'unlock-selection', p: SelectionProjectReadModel): void
    }>()

    // ---------------- local state ----------------

    const projectsDataView = ref<'CBA' | 'RAG' | 'Ranking' | 'H2H'>('CBA')
    const criteriaFilter = ref<'Objectives' | 'Attributes' | 'All criteria'>('Objectives')
    const valueFilter = ref<number | null>(null)
    const criterionId = ref<number | null>(null)

    const criterionSelectorDialog = new CriterionSelectorDialogViewModel()

    // ---------------- computed ----------------

    const currencyLabel = computed(() => props.propCurrencyLabel)
    const projects = computed(() => props.propProjects)
    const allCriteria = computed(() => props.propAllCriteria)
    const comparableScenarios = computed(() => props.propComparableScenarios)
    const searchPhrase = computed(() => props.propSearchPhrase ?? null)
    const projectsFilter = computed(() => props.propProjectsFilter ?? null)

    const criteria = computed<SelectionCriterionReadModel[]>(() =>
    {
        if (criteriaFilter.value === 'Objectives')
            return allCriteria.value.filter(a => a.IsTop)

        if (criteriaFilter.value === 'Attributes')
            return allCriteria.value.filter(a => a.IsBottom)

        if (criteriaFilter.value === 'All criteria')
            return allCriteria.value.filter(a => !a.IsGoal)

        return []
    })

    const comparableProjects = computed(() =>
        [...projects.value]
            .sort((a, b) =>
                numCompare(
                    a.ComparableScenariosAcceptanceValue,
                    b.ComparableScenariosAcceptanceValue
                )
            )
            .reverse()
    )

    const criteriaFilterVisible = computed(() => projectsDataView.value === 'RAG')
    const criterionSelectorVisible = computed(() => projectsDataView.value === 'Ranking')
    const valueFilterVisible = computed(() => projectsDataView.value === 'Ranking')

    const valueFilterLabel = computed(() =>
        valueFilter.value == null ? 'All' : `Value > ${valueFilter.value}`
    )

    const selectedCriterion = computed<SelectionCriterionReadModel>(() =>
        allCriteria.value.find(a => a.Id === criterionId.value)
        ?? allCriteria.value.find(a => a.ParentId == null)!
    )

    const rankingProjects = computed(() =>
    {
        let view = [...projects.value]

        if (valueFilter.value != null)
        {
            view = view.filter(p =>
                p.NormalizedCriteriaValues[selectedCriterion.value.ValueIndex] >= valueFilter.value!
            )
        }

        return view
            .sort((a, b) =>
                numCompare(
                    a.NormalizedCriteriaValues[selectedCriterion.value.ValueIndex],
                    b.NormalizedCriteriaValues[selectedCriterion.value.ValueIndex]
                )
            )
            .reverse()
    })

    const subcriteria = computed(() =>
        allCriteria.value.filter(a => a.ParentId === selectedCriterion.value.Id)
    )

    const criterionLabel = computed(() => selectedCriterion.value.Name)

    // ---------------- actions / helpers ----------------

    function updateSearchPhrase(value: string | null)
    {
        emit('update-search-phrase', value)
    }

    function updateProjectsFilter(filter: string)
    {
        emit('update-projects-filter', filter)
    }

    function showCriterionSelector()
    {
        criterionSelectorDialog.show(criterionId.value, allCriteria.value)
    }

    function formatValue(val: number | null): string
    {
        return val?.toLocaleString('en', { maximumFractionDigits: 1 }) ?? ''
    }

    function getRagClass(val: number | null): string
    {
        if (val == null) return ''

        if (val < 25) return 'table-danger'
        if (val < 75) return 'table-warning'
        return 'table-success'
    }

    function getCompareAcceptanceClass(val: string | null): string
    {
        if (val === ProjectAcceptance.Rejected.toString()) return 'table-danger'
        if (val === ProjectAcceptance.TBD.toString()) return 'table-warning'
        if (val === ProjectAcceptance.Accepted.toString()) return 'table-success'
        return ''
    }

    function getCriterionBarColor(c: SelectionCriterionReadModel): string
    {
        return getDistinctBgCss(c.ValueIndex)
    }

    function getCriterionColor(c: SelectionCriterionReadModel): string
    {
        return getDistinctColor(c.ValueIndex)
    }

    function getAcceptanceButtonCss(p: SelectionProjectReadModel, btn: 'Accepted' | 'Rejected' | 'TBD')
    {
        const base = 'btn btn-sm btn-secondary'
        return p.UiAcceptance === btn ? base : 'btn btn-sm btn-outline-secondary'
    }

    function acceptSelection(p: SelectionProjectReadModel)
    {
        emit('accept-selection', p)
    }

    function rejectSelection(p: SelectionProjectReadModel)
    {
        emit('reject-selection', p)
    }

    function lockSelection(p: SelectionProjectReadModel)
    {
        emit('lock-selection', p)
    }

    function unlockSelection(p: SelectionProjectReadModel)
    {
        emit('unlock-selection', p)
    }
</script>


<template>
    <div>
        <CriterionSelectorDialog :viewModel="criterionSelectorDialog"
                                 @confirm-selection="criterionId = $event" />

        <nav class="navbar navbar-light navbar-expand-md" style="background-color: rgba(0, 0, 0, 0.03); border: 1px solid rgba(0, 0, 0, 0.125); border-bottom: 0; " role="navigation">
            <ul class="navbar-nav mr-2">
                <li class="dropdown">
                    <div class="navbar-form">
                        <div class="btn-group">
                            <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">{{projectsDataView}}</button>
                            <div class="dropdown-menu">
                                <a class="dropdown-item" @click="projectsDataView = 'CBA'">Cost-Benefit Analysis</a>
                                <a class="dropdown-item" @click="projectsDataView = 'RAG'">Heatmap</a>
                                <a class="dropdown-item" @click="projectsDataView = 'Ranking'">Ranking</a>
                                <a class="dropdown-item" @click="projectsDataView = 'H2H'">Compare scenarios</a>
                            </div>
                        </div>
                    </div>
                </li>
                <li class="dropdown ml-2" v-if="criteriaFilterVisible === true">
                    <div class="navbar-form">
                        <div class="btn-group">
                            <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">{{criteriaFilter}}</button>
                            <div class="dropdown-menu">
                                <a class="dropdown-item" @click="criteriaFilter = 'Objectives'">Objectives (top)</a>
                                <a class="dropdown-item" @click="criteriaFilter = 'Attributes'">Attributes (bottom)</a>
                                <a class="dropdown-item" @click="criteriaFilter = 'All criteria'">All criteria</a>
                            </div>
                        </div>
                    </div>
                </li>
                <li class="dropdown ml-2" v-if="criterionSelectorVisible === true">
                    <div class="navbar-form">
                        <button class="btn btn-outline-secondary" type="button" @click="showCriterionSelector">Criterion</button>
                    </div>
                </li>
                <li class="dropdown ml-2" v-if="valueFilterVisible === true">
                    <div class="navbar-form">
                        <div class="dropdown">
                            <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">Value: {{valueFilter ?? 'Any'}}</button>
                            <div class="dropdown-menu" style="max-height: 250px; overflow-y: auto;">
                                <a class="dropdown-item" @click="valueFilter = null">All</a>
                                <h6 class="dropdown-header">By value</h6>
                                <a class="dropdown-item" @click="valueFilter = 75">Above 75</a>
                                <a class="dropdown-item" @click="valueFilter = 50">Above 50</a>
                            </div>
                        </div>
                    </div>
                </li>

            </ul>

            <ul class="navbar-nav ml-auto mr-2">
                <li class="dropdown">
                    <div class="navbar-form">
                        <div class="btn-group">
                            <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">Filter: {{projectsFilter}}</button>
                            <div class="dropdown-menu">
                                <a class="dropdown-item" @click="updateProjectsFilter('All')">All</a>
                                <div class="dropdown-divider"></div>
                                <h6 class="dropdown-header">Filter by status:</h6>
                                <a class="dropdown-item" @click="updateProjectsFilter('TBD')">TBD</a>
                                <a class="dropdown-item" @click="updateProjectsFilter('Accepted')">Accepted</a>
                                <a class="dropdown-item" @click="updateProjectsFilter('Rejected')">Rejected</a>
                            </div>
                        </div>
                    </div>
                </li>
            </ul>

            <form class="form-inline my-2 my-lg-0" role="search">
                <input class="form-control mr-sm-2" type="search" placeholder="Search" :value="searchPhrase" @input="updateSearchPhrase($event.target.value)">
            </form>
        </nav>

        <div v-if="projectsDataView === 'CBA'">
            <div class="table-responsive">
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th class="align-middle" style="min-width: 300px; width: 300px; max-width: 300px;">Project</th>
                            <th class="text-center align-middle" style="min-width: 100px; width: 100px; max-width: 100px;">P&L</th>
                            <th class="text-center align-middle" style="min-width: 250px; width: 250px; max-width: 250px;">Selection</th>
                            <th class="text-center align-middle" style="min-width: 200px; width: 200px; max-width: 200px;">Cost of value point <br /> ({{currencyLabel}})</th>
                            <th class="text-right align-middle" style="min-width: 250px; width: auto;">Costs <br /> ({{currencyLabel}})</th>
                            <th class="align-middle" style="min-width: 250px; width: auto; ">Value <br /> (0-100)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="a in projects" :key="a.Id">
                            <td style="min-width: 300px; width: 300px; max-width: 300px; ">
                                <a class="text-primary" v-sanitizedHref="a.DashboardUrl">
                                    {{a.Name}}
                                </a>
                            </td>
                            <td class="text-center align-middle" style="min-width: 100px; width: 100px; max-width: 100px; ">
                                <a v-if="a.PlanId" class="text-primary" v-sanitizedHref="a.PnlUrl">
                                    View
                                </a>
                                <a v-else class="text-danger" v-sanitizedHref="a.PlansUrl">
                                    NA
                                </a>
                            </td>
                            <td class="text-center align-middle" style="min-width: 250px; width: 250px; max-width: 250px; ">
                                <div class="btn-group mr-2" role="group">
                                    <button type="button" :class="getAcceptanceButtonCss(a, 'Accepted')" @click="acceptSelection(a)" :disabled="a.UiAcceptanceLocked === true">Accept</button>
                                    <button type="button" :class="getAcceptanceButtonCss(a, 'Rejected')" @click="rejectSelection(a)" :disabled="a.UiAcceptanceLocked === true">Reject</button>
                                </div>
                                <button v-if="a.UiAcceptanceLocked === true" class="btn btn-outline-secondary btn-sm" @click="unlockSelection(a)">
                                    <i class="fa-solid fa-lock"></i>
                                </button>
                                <button v-if="a.UiAcceptanceLocked === false" class="btn btn-outline-secondary btn-sm"
                                        title="Click to lock selection status" :disabled="a.UiAcceptance === 'TBD'" @click="lockSelection(a)">
                                    <i class="fa-solid fa-lock-open"></i>
                                </button>
                            </td>
                            <td class="text-center align-middle" style="min-width: 200px; width: 200px; max-width: 200px;">
                                {{a.CostOfValuePointString}}
                            </td>
                            <td class="text-right align-middle" style="min-width: 250px; width: auto; ">
                                <div class="progress justify-content-end" style="height: 30px;">
                                    <div class="progress-bar bg-danger" role="progressbar" :style="{width: a.NormalizedTotalCost + '%'}">
                                        {{a.TotalCostString}}
                                    </div>
                                </div>
                            </td>
                            <td class="align-middle" style="min-width: 250px; width: auto;">
                                <div class="progress" style="height: 30px;">
                                    <div class="progress-bar bg-success" role="progressbar" :style="{width: a.TotalValue + '%'}">
                                        {{a.TotalValueString}}
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

        </div>

        <div v-if="projectsDataView === 'RAG'">

            <div class="table-responsive">
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th class="align-middle" style="min-width: 300px; width: 300px; max-width: 300px;">Project</th>
                            <th class="text-center align-middle" style="min-width: 100px; width: 100px; max-width: 100px;">P&L</th>
                            <th class="text-center align-middle" style="min-width: 250px; width: 250px; max-width: 250px;">Selection</th>
                            <th class="text-center align-middle" v-for="c in criteria" :key="c.Id" style="min-width: 300px; width: auto;">{{c.Name}}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="a in projects" :key="a.Id">
                            <td style="min-width: 300px; width: 300px; max-width: 300px; ">
                                <a class="text-primary" v-sanitizedHref="a.DashboardUrl">
                                    {{a.Name}}
                                </a>
                            </td>
                            <td class="text-center align-middle" style="min-width: 100px; width: 100px; max-width: 100px; ">
                                <a v-if="a.PlanId" class="text-primary" v-sanitizedHref="a.PnlUrl">
                                    View
                                </a>
                                <a v-else class="text-danger" v-sanitizedHref="a.PlansUrl">
                                    NA
                                </a>
                            </td>
                            <td class="text-center align-middle" style="min-width: 250px; width: 250px; max-width: 250px; ">
                                <div class="btn-group mr-2" role="group">
                                    <button type="button" :class="getAcceptanceButtonCss(a, 'Accepted')" @click="acceptSelection(a)" :disabled="a.UiAcceptanceLocked === true">Accept</button>
                                    <button type="button" :class="getAcceptanceButtonCss(a, 'Rejected')" @click="rejectSelection(a)" :disabled="a.UiAcceptanceLocked === true">Reject</button>
                                </div>
                                <button v-if="a.UiAcceptanceLocked === true" class="btn btn-outline-secondary btn-sm" @click="unlockSelection(a)">
                                    <i class="fa-solid fa-lock"></i>
                                </button>
                                <button v-if="a.UiAcceptanceLocked === false" class="btn btn-outline-secondary btn-sm"
                                        title="Click to lock selection status" :disabled="a.UiAcceptance === 'TBD'" @click="lockSelection(a)">
                                    <i class="fa-solid fa-lock-open"></i>
                                </button>
                            </td>
                            <td class="text-center align-middle" v-for="c in criteria" :key="c.Id" style="min-width: 300px; width: auto;"
                                :class="getRagClass(a.NormalizedCriteriaValues[c.ValueIndex])">
                                {{formatValue(a.NormalizedCriteriaValues[c.ValueIndex])}}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-if="projectsDataView === 'H2H'">

            <div class="table-responsive">
                <table class="table table-bordered table-hover">
                    <thead>
                        <tr>
                            <th class="align-middle" style="min-width: 300px; width: 300px; max-width: 300px;">Project</th>
                            <th class="text-center align-middle" style="min-width: 100px; width: 100px; max-width: 100px;">P&L</th>
                            <th class="text-center align-middle" style="min-width: 250px; width: 250px; max-width: 250px;">Selection</th>
                            <th v-for="s in comparableScenarios" :key="s.Id">{{s.Name}}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="a in comparableProjects" :key="a.Id">
                            <td style="min-width: 300px; width: 300px; max-width: 300px; ">
                                <a class="text-primary" v-sanitizedHref="a.DashboardUrl">
                                    {{a.Name}}
                                </a>
                            </td>
                            <td class="text-center align-middle" style="min-width: 100px; width: 100px; max-width: 100px; ">
                                <a v-if="a.PlanId" class="text-primary" v-sanitizedHref="a.PnlUrl">
                                    View
                                </a>
                                <a v-else class="text-danger" v-sanitizedHref="a.PlansUrl">
                                    NA
                                </a>
                            </td>
                            <td class="text-center align-middle" style="min-width: 250px; width: 250px; max-width: 250px; ">
                                <div class="btn-group mr-2" role="group">
                                    <button type="button" :class="getAcceptanceButtonCss(a, 'Accepted')" @click="acceptSelection(a)" :disabled="a.UiAcceptanceLocked === true">Accept</button>
                                    <button type="button" :class="getAcceptanceButtonCss(a, 'Rejected')" @click="rejectSelection(a)" :disabled="a.UiAcceptanceLocked === true">Reject</button>
                                </div>
                                <button v-if="a.UiAcceptanceLocked === true" class="btn btn-outline-secondary btn-sm" @click="unlockSelection(a)">
                                    <i class="fa-solid fa-lock"></i>
                                </button>
                                <button v-if="a.UiAcceptanceLocked === false" class="btn btn-outline-secondary btn-sm"
                                        title="Click to lock selection status" :disabled="a.UiAcceptance === 'TBD'" @click="lockSelection(a)">
                                    <i class="fa-solid fa-lock-open"></i>
                                </button>
                            </td>
                            <td v-for="s in comparableScenarios" :key="s.Id" class="align-middle" style="min-width: 250px; width: auto;"
                                :class="getCompareAcceptanceClass(a.ComparableScenariosAcceptance[s.ValueIndex])">
                                {{a.ComparableScenariosAcceptance[s.ValueIndex]}}
                            </td>

                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-if="projectsDataView === 'Ranking'">
            <table class="table table-bordered table-hover mt-2">
                <thead>
                    <tr>
                        <th class="align-middle" style="min-width: 300px; width: 300px; max-width: 300px;">Project</th>
                        <th class="text-center align-middle" style="min-width: 100px; width: 100px; max-width: 100px;">P&L</th>
                        <th class="text-center align-middle" style="min-width: 250px; width: 250px; max-width: 250px;">Selection</th>
                        <th class="text-center align-middle" style="min-width: 150px; width: 150px; max-width: 150px;">Value</th>
                        <th class="align-middle" style="min-width: 250px; width: auto;">
                            {{criterionLabel}}
                            <div class="float-right" v-if="selectedCriterion.IsBottom === false">
                                <div class="dropdown d-inline mr-2">
                                    <button data-toggle="dropdown" class="btn btn-secondary btn-sm">
                                        Legend
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-right" role="menu" style="width: 300px;">
                                        <span class="dropdown-item-text font-weight-normal" v-for="c in subcriteria" :key="c.Id">
                                            <i class="fa fa-square" aria-hidden="true" :style="{color: getCriterionColor(c)}"></i>
                                            {{c.Name}}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="a in rankingProjects" :key="a.Id">
                        <td style="min-width: 300px; width: 300px; max-width: 300px; ">
                            <a class="text-primary" v-sanitizedHref="a.DashboardUrl">
                                {{a.Name}}
                            </a>
                        </td>
                        <td class="text-center align-middle" style="min-width: 100px; width: 100px; max-width: 100px; ">
                            <a v-if="a.HasActivePlan" class="text-primary" v-sanitizedHref="a.PnlUrl">
                                View
                            </a>
                            <a v-else class="text-danger" v-sanitizedHref="a.PlansUrl">
                                NA
                            </a>
                        </td>
                        <td class="text-center align-middle" style="min-width: 250px; width: 250px; max-width: 250px; ">
                            <div class="btn-group mr-2" role="group">
                                <button type="button" :class="getAcceptanceButtonCss(a, 'Accepted')" @click="acceptSelection(a)" :disabled="a.UiAcceptanceLocked === true">Accept</button>
                                <button type="button" :class="getAcceptanceButtonCss(a, 'Rejected')" @click="rejectSelection(a)" :disabled="a.UiAcceptanceLocked === true">Reject</button>
                            </div>
                            <button v-if="a.UiAcceptanceLocked === true" class="btn btn-outline-secondary btn-sm" @click="unlockSelection(a)">
                                <i class="fa-solid fa-lock"></i>
                            </button>
                            <button v-if="a.UiAcceptanceLocked === false" class="btn btn-outline-secondary btn-sm"
                                    title="Click to lock selection status" :disabled="a.UiAcceptance === 'TBD'" @click="lockSelection(a)">
                                <i class="fa-solid fa-lock-open"></i>
                            </button>
                        </td>
                        <td class="text-center align-middle" style="min-width: 150px; width: 150px; max-width: 150px;">
                            {{formatValue(a.NormalizedCriteriaValues[selectedCriterion.ValueIndex])}}
                        </td>
                        <td>
                            <div v-if="!selectedCriterion.IsBottom" class="progress" style="height: 30px;">
                                <div v-for="c in subcriteria" :key="c.Id" :title="c.Name" class="progress-bar" role="progressbar" :class="getCriterionBarColor(c)"
                                     :style="{width: a.LocalWeightedCriteriaValues[c.ValueIndex] + '%'}"></div>
                            </div>
                            <div v-if="selectedCriterion.IsBottom" class="progress" style="height: 30px;">
                                <div class="progress-bar" role="progressbar" :style="{width: a.NormalizedCriteriaValues[selectedCriterion.ValueIndex] + '%'}"></div>
                            </div>
                        </td>

                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>